import React, { useEffect, useState, useMemo } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import Logo from "../components/Logo";

// ────────────────────────────────────────────────────────────────────────
// How Much Home Can I Afford?
//
// IMPORTANT — THESE RULES ARE LOCKED BY THE BROKER:
//   • Calculation logic, formulas, numbers, and input defaults
//     (rate 6.5, down 3, tax 1.0, pmi 0.7, ins 1800, dti 45, term 30,
//      close 2.5, sp 20) must stay EXACTLY as supplied.
//   • Every word in the disclaimer / footer block must appear unchanged.
//   • CORE brand palette is enforced — Dusty Rose primary / Rose hover /
//     Grey borders / Black text / White backgrounds.
//
// Lead-form submissions route to the SAME public endpoint the /clients page
// uses — `POST /api/public/client-lead` — so calculator leads flow through
// the existing round-robin auto-assign + alert emails to info@ and lawinfrey@
// without any extra backend plumbing.
// ────────────────────────────────────────────────────────────────────────

// Brand palette — single source of truth for this page.
const DUSTY_ROSE = "#bf858e"; // primary accent
const ROSE       = "#e1b7c1"; // hover / highlight
const GREY       = "#d1cece"; // borders / dividers
const INK        = "#050303"; // text
const WHITE      = "#ffffff"; // backgrounds
const SOFT_ROSE  = "rgba(225, 183, 193, 0.22)"; // result bar tint

const DEFAULTS = {
  inc1: "", inc2: "", debt: "",
  dti: "45",
  rate: "6.5", term: "30", down: "3", tax: "1.0",
  ins: "1800", hoa: "0", pmi: "0.7",
  close: "2.5", sav: "",
  sp: "20",
  nm: "", em: "", ph: "", tl: "6–12 months", consent: false,
};

const TIMELINE_OPTS = ["0–3 months", "3–6 months", "6–12 months", "12+ months"];

const usd = (v) => "$" + Math.round(v).toLocaleString("en-US");
const num = (v) => Math.max(0, parseFloat(v) || 0);

export default function Calculator() {
  const [f, setF] = useState(DEFAULTS);
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [err, setErr] = useState("");

  // Browser-tab title — crawlers get the OG tags from public/index.html.
  useEffect(() => {
    const prev = document.title;
    document.title = "How Much Home Can I Afford? | CORE Realty and Associates";
    return () => { document.title = prev; };
  }, []);

  const set = (k) => (e) =>
    setF((p) => ({ ...p, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  // ───────────────────────────────────────────────────────────────
  // calc() — ported verbatim from the broker's HTML source. Do NOT
  // tweak the formulas or constants without a direct ask from the
  // broker; the output appears in printable marketing materials.
  // ───────────────────────────────────────────────────────────────
  const results = useMemo(() => {
    const inc  = num(f.inc1) + num(f.inc2);
    const debt = num(f.debt);
    const front = inc * 0.28;
    const back  = (inc * num(f.dti)) / 100 - debt;
    const maxPmt = Math.max(0, Math.min(front, back));

    const r = num(f.rate) / 1200;
    const N = (num(f.term) * 12) || 360;
    const fFactor = r ? (r * Math.pow(1 + r, N)) / (Math.pow(1 + r, N) - 1) : 1 / N;
    const d = num(f.down) / 100;
    const k = (1 - d) * fFactor
            + num(f.tax) / 1200
            + (d < 0.2 ? (num(f.pmi) / 1200) * (1 - d) : 0);
    const price = Math.max(0, (maxPmt - num(f.hoa) - num(f.ins) / 12) / k);
    const quick = (maxPmt / 7.5) * 1000;

    const cash = price * (d + num(f.close) / 100);
    const need = Math.max(0, cash - num(f.sav));

    const save = (inc * num(f.sp)) / 100;
    let timeTxt = "—", timeNote = "";
    if (inc) {
      if (need <= 0) {
        timeTxt = "Ready now";
        timeNote = "Your savings cover the estimated cash needed.";
      } else if (save <= 0) {
        timeNote = "Enter a savings percentage above 0.";
      } else {
        const m = Math.ceil(need / save);
        const y = Math.floor(m / 12);
        const mo = m % 12;
        timeTxt = (y ? y + " yr " : "") + mo + " mo";
        timeNote = "Saving " + usd(save) + "/month toward " + usd(need) + ".";
      }
    }

    return {
      inc, debt, front, back, maxPmt, price, quick, cash, need,
      d, timeTxt, timeNote,
      r1Text: inc ? usd(front) + "/mo" : "—",
      r2Text: inc ? usd(maxPmt) + "/mo" : "—",
      r2Note: !inc
        ? "Enter your income above."
        : (back < front
            ? "Your existing debts are the limiting factor — paying them down raises this number."
            : "The 28% housing guideline is the limiting factor."),
      r3Text: inc ? usd(price) : "—",
      r3Note: inc
        ? "Includes principal, interest, taxes, insurance" + (d < 0.2 ? ", mortgage insurance" : "") +
          ". Quick rule-of-thumb check ($7.50 per $1,000): " + usd(quick) + "."
        : "",
      r4Text: inc ? usd(cash) : "—",
      r4Note: inc
        ? "That is " + ((d + num(f.close) / 100) * 100).toFixed(1) + "% of the price" +
          (num(f.sav) ? "; " + usd(need) + " still to save." : ".")
        : "",
    };
  }, [f]);

  // ───────── Lead submit → /api/public/client-lead ─────────
  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!f.nm.trim() || !/^\S+@\S+\.\S+$/.test(f.em)) {
      setErr("Please enter your name and a valid email.");
      return;
    }
    if (!f.consent) {
      setErr("Please check the consent box to continue.");
      return;
    }

    // Pack calculator numbers into the free-text message field so the
    // broker sees the full snapshot alongside the lead in Contacts.
    const summary = [
      "— Submitted via Homebuyer Calculator —",
      "Gross monthly income: " + usd(results.inc),
      "Monthly debts: " + usd(results.debt),
      "Max monthly payment: " + usd(results.maxPmt),
      "Est. home price: " + usd(results.price),
      "Cash needed to buy: " + usd(results.cash),
      "Time to save remaining cash: " + results.timeTxt,
      "",
      "Interest rate: " + f.rate + "% · " + f.term + "yr · Down " + f.down + "%",
      "Property tax: " + f.tax + "% · Insurance $" + f.ins + "/yr · PMI " + f.pmi + "%",
      "DTI target: " + f.dti + "% · Monthly savings rate: " + f.sp + "%",
    ].join("\n");

    setSending(true);
    try {
      await api.post("/public/client-lead", {
        name: f.nm.trim(),
        email: f.em.trim(),
        phone: f.ph.trim(),
        intent: "buy",
        timeline: f.tl,
        neighborhood: "",
        message: summary,
        // Attach the full calculator state so the backend generates and emails
        // the branded Buyer Workbook PDF to the buyer AND the on-duty agent.
        calculator: {
          inc1: f.inc1, inc2: f.inc2, debt: f.debt,
          dti: f.dti, rate: f.rate, term: f.term, down: f.down,
          tax: f.tax, ins: f.ins, hoa: f.hoa, pmi: f.pmi,
          close: f.close, sav: f.sav, sp: f.sp,
        },
      });
      setSent(true);
      toast.success("Plan request sent — your Workbook is on its way to your inbox");
    } catch {
      setErr("Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  // ───────── Workbook PDF download → /api/public/calculator/workbook ─────────
  // Self-service, no email required. The backend recomputes everything from
  // the same inputs so the printed numbers always match what's on screen.
  const downloadWorkbook = async () => {
    setDownloading(true);
    try {
      const payload = {
        name: f.nm.trim(),
        email: f.em.trim(),
        phone: f.ph.trim(),
        timeline: f.tl,
        inc1: f.inc1, inc2: f.inc2, debt: f.debt,
        dti: f.dti, rate: f.rate, term: f.term, down: f.down,
        tax: f.tax, ins: f.ins, hoa: f.hoa, pmi: f.pmi,
        close: f.close, sav: f.sav, sp: f.sp,
      };
      const res = await api.post("/public/calculator/workbook", payload, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      const safe = (f.nm.trim() || "Homebuyer").replace(/[^\w-]+/g, "_");
      a.href = url;
      a.download = `CORE_Workbook_${safe}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Workbook downloaded — bring it to your lender");
    } catch {
      toast.error("Couldn't generate the workbook. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="calculator-page min-h-screen bg-white" data-testid="calculator-page">
      {/* Public header — mirrors /clients */}
      <header className="border-b border-[#d1cece] px-8 py-5 flex items-center justify-between">
        <Logo variant="black" height={44} />
        <div className="flex items-center gap-6 text-sm">
          <a href="/clients" className="hover:text-[#bf858e]" data-testid="calculator-nav-clients">For Buyers & Sellers →</a>
          <a href="/login" className="hover:text-[#bf858e]" data-testid="calculator-agent-login">Agent Login →</a>
        </div>
      </header>

      {/* Hero — same layout / height / overlay treatment as /clients, with
          a bright daytime Atlanta skyline image. */}
      <section
        className="relative px-8 lg:px-16 py-24 lg:py-32 overflow-hidden bg-[#050303]"
        data-testid="calculator-hero"
      >
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            // Bright, sunny suburban luxury-home exterior — brick + stone with
            // emerald lawn and clear blue sky; broker-supplied hero image that
            // matches the Metro Atlanta / Buckhead curb-appeal aesthetic.
            backgroundImage:
              "url('https://customer-assets-39nsmqrw.emergentagent.net/job_core-agent-ops/artifacts/c0795de1cef09676_IMG-4243.JPG')",
          }}
          aria-hidden="true"
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-[#050303]/75 via-[#050303]/35 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#050303]/30" />
        <div className="relative max-w-4xl text-white" style={{ textShadow: "0 2px 24px rgba(0,0,0,0.55)" }}>
          <div className="mb-3 text-[11px] lg:text-xs font-bold tracking-[0.25em] uppercase text-[#e1b7c1]">
            Real Estate Integrity At Its CORE
          </div>
          <div className="overline mb-4 text-white/80 font-semibold">Free Mortgage Affordability Tool</div>
          <h1 className="font-heading font-bold text-5xl lg:text-7xl leading-[1.05] tracking-tight text-white">
            How Much Home Can I Afford?
          </h1>
          <p className="mt-6 text-lg lg:text-xl font-medium text-white max-w-2xl">
            Five quick steps. Estimates update as you type.
          </p>
        </div>
      </section>

      {/* Calculator body — CORE brand palette applied. The source HTML's
          card vocabulary is preserved; only the chrome is restyled. */}
      <main
        className="max-w-2xl mx-auto px-4 py-10"
        style={{ fontFamily: 'Montserrat, Arial, sans-serif', color: INK, lineHeight: 1.5 }}
      >
        {/* Focus / hover states (inline styles can't do pseudo-classes). */}
        <style>{`
          .calculator-page input:focus,
          .calculator-page select:focus {
            outline: none;
            border-color: ${DUSTY_ROSE};
            box-shadow: 0 0 0 3px ${ROSE}80;
          }
          .calc-cta:hover:not(:disabled) {
            background: ${ROSE};
            color: ${INK};
          }
          .calc-cta:focus-visible {
            outline: none;
            box-shadow: 0 0 0 3px ${ROSE};
          }
          .calc-ghost:hover:not(:disabled) {
            background: ${ROSE};
            color: ${INK};
            border-color: ${ROSE};
          }
          .calc-ghost:focus-visible {
            outline: none;
            box-shadow: 0 0 0 3px ${ROSE};
          }
          .calc-ghost:disabled, .calc-cta:disabled {
            opacity: 0.6;
            cursor: not-allowed;
          }
          .calc-summary:hover { color: ${ROSE}; }
        `}</style>
        {/* ───────── 1 · income & debts ───────── */}
        <Card>
          <CardHeading num={1}>Your income &amp; debts</CardHeading>
          <Grid>
            <Field label="Your gross monthly income ($)">
              <input id="inc1" type="number" inputMode="decimal" placeholder="5,000"
                     value={f.inc1} onChange={set("inc1")} style={inputS}
                     data-testid="calc-inc1" />
            </Field>
            <Field label="Spouse/co-buyer gross monthly income ($)">
              <input id="inc2" type="number" inputMode="decimal" placeholder="0"
                     value={f.inc2} onChange={set("inc2")} style={inputS}
                     data-testid="calc-inc2" />
            </Field>
            <Field label="Monthly debt payments ($) — car, cards, student loans (minimums)" fullWidth>
              <input id="debt" type="number" inputMode="decimal" placeholder="500"
                     value={f.debt} onChange={set("debt")} style={inputS}
                     data-testid="calc-debt" />
            </Field>
          </Grid>
          <Result>
            Housing payment guideline (28% of income):{" "}
            <b data-testid="calc-r1">{results.r1Text}</b>
          </Result>
        </Card>

        {/* ───────── 2 · max payment ───────── */}
        <Card>
          <CardHeading num={2}>Maximum payment you may qualify for</CardHeading>
          <Grid>
            <Field label="Total debt limit (all debts + new housing payment)" fullWidth>
              <select id="dti" value={f.dti} onChange={set("dti")} style={inputS} data-testid="calc-dti">
                <option value="36">36% — conservative</option>
                <option value="43">43% — common maximum</option>
                <option value="45">45% — stretch (some loan programs)</option>
              </select>
            </Field>
          </Grid>
          <Result>
            Max monthly housing payment:{" "}
            <span style={bigS} data-testid="calc-r2">{results.r2Text}</span>
            <div style={smallS} data-testid="calc-r2-note">{results.r2Note}</div>
          </Result>
        </Card>

        {/* ───────── 3 · estimated price ───────── */}
        <Card>
          <CardHeading num={3}>Estimated home price</CardHeading>
          <Grid>
            <Field label="Interest rate (%)">
              <input type="number" step="0.125" value={f.rate} onChange={set("rate")} style={inputS}
                     data-testid="calc-rate" />
            </Field>
            <Field label="Loan term">
              <select value={f.term} onChange={set("term")} style={inputS} data-testid="calc-term">
                <option value="30">30 years</option>
                <option value="15">15 years</option>
              </select>
            </Field>
            <Field label="Down payment (%)">
              <input type="number" step="0.5" value={f.down} onChange={set("down")} style={inputS}
                     data-testid="calc-down" />
            </Field>
            <Field label="Property tax (% of price / yr)">
              <input type="number" step="0.05" value={f.tax} onChange={set("tax")} style={inputS}
                     data-testid="calc-tax" />
            </Field>
          </Grid>
          <details style={{ marginTop: 12 }}>
            <summary className="calc-summary" style={{ cursor: "pointer", color: DUSTY_ROSE, fontWeight: 600, fontSize: 14, transition: "color 160ms ease" }}>
              More assumptions
            </summary>
            <Grid>
              <Field label="Homeowners insurance ($/yr)">
                <input type="number" value={f.ins} onChange={set("ins")} style={inputS}
                       data-testid="calc-ins" />
              </Field>
              <Field label="HOA dues ($/mo)">
                <input type="number" value={f.hoa} onChange={set("hoa")} style={inputS}
                       data-testid="calc-hoa" />
              </Field>
              <Field label="Mortgage insurance (% of loan / yr, if <20% down)">
                <input type="number" step="0.05" value={f.pmi} onChange={set("pmi")} style={inputS}
                       data-testid="calc-pmi" />
              </Field>
            </Grid>
          </details>
          <Result>
            Estimated home price:{" "}
            <span style={bigS} data-testid="calc-r3">{results.r3Text}</span>
            <div style={smallS} data-testid="calc-r3-note">{results.r3Note}</div>
          </Result>
        </Card>

        {/* ───────── 4 · cash needed ───────── */}
        <Card>
          <CardHeading num={4}>Cash needed to buy</CardHeading>
          <Grid>
            <Field label="Closing costs (% of price)">
              <input type="number" step="0.25" value={f.close} onChange={set("close")} style={inputS}
                     data-testid="calc-close" />
            </Field>
            <Field label="Savings you already have ($)">
              <input type="number" placeholder="0" value={f.sav} onChange={set("sav")} style={inputS}
                     data-testid="calc-sav" />
            </Field>
          </Grid>
          <Result>
            Down payment + closing costs:{" "}
            <span style={bigS} data-testid="calc-r4">{results.r4Text}</span>
            <div style={smallS} data-testid="calc-r4-note">{results.r4Note}</div>
          </Result>
        </Card>

        {/* ───────── 5 · time to save ───────── */}
        <Card>
          <CardHeading num={5}>Time to get there</CardHeading>
          <Grid>
            <Field label="Share of gross income you can save each month (%)" fullWidth>
              <input type="number" step="1" value={f.sp} onChange={set("sp")} style={inputS}
                     data-testid="calc-sp" />
            </Field>
          </Grid>
          <Result>
            Time to save the remaining cash:{" "}
            <span style={bigS} data-testid="calc-r5">{results.timeTxt}</span>
            <div style={smallS} data-testid="calc-r5-note">{results.timeNote}</div>
          </Result>
        </Card>

        {/* ───────── Workbook PDF — self-service, no email required ───────── */}
        <Card data-testid="calc-workbook-card">
          <div style={{ display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap" }}>
            <div style={{ flex: "1 1 260px", minWidth: 220 }}>
              <h2 style={{ ...h2S, fontSize: 20, marginBottom: 6 }}>Keep a copy of your plan</h2>
              <p style={{ ...smallS, margin: 0 }}>
                Download a one-page CORE Realty workbook with your estimated price, cash
                to close, and timeline — ready to bring to your lender.
              </p>
            </div>
            <button
              type="button"
              onClick={downloadWorkbook}
              disabled={downloading}
              className="calc-ghost"
              style={ghostButtonS}
              data-testid="calc-workbook-btn"
            >
              {downloading ? "Preparing…" : "Download Buyer Workbook (PDF)"}
            </button>
          </div>
        </Card>

        {/* ───────── CTA / lead form ───────── */}
        <Card ctaRose data-testid="calc-leadcard">
          <h2 style={{ ...h2S, fontSize: 23 }}>Get your personalized game plan</h2>
          <p style={smallS}>
            A CORE Realty agent can review your numbers, connect you with trusted
            lenders, and share down payment assistance options for Georgia buyers.
          </p>
          {sent ? (
            <div data-testid="calc-thanks">
              <div style={resultS}>
                <b>Thank you!</b> A CORE Realty agent will reach out shortly — your personalized Buyer Workbook PDF is on its way to <b>{f.em.trim() || "your inbox"}</b>.
              </div>
              <button
                type="button"
                onClick={downloadWorkbook}
                disabled={downloading}
                className="calc-ghost"
                style={{ ...ghostButtonS, width: "100%", marginTop: 14 }}
                data-testid="calc-workbook-btn-thanks"
              >
                {downloading ? "Preparing…" : "Also download it now"}
              </button>
            </div>
          ) : (
            <form onSubmit={submit} noValidate data-testid="calc-lead-form">
              <Grid>
                <Field label="Full name">
                  <input required autoComplete="name" value={f.nm} onChange={set("nm")} style={inputS}
                         data-testid="calc-nm" />
                </Field>
                <Field label="Email">
                  <input type="email" required autoComplete="email" value={f.em} onChange={set("em")} style={inputS}
                         data-testid="calc-em" />
                </Field>
                <Field label="Phone">
                  <input type="tel" autoComplete="tel" value={f.ph} onChange={set("ph")} style={inputS}
                         data-testid="calc-ph" />
                </Field>
                <Field label="When do you plan to buy?">
                  <select value={f.tl} onChange={set("tl")} style={inputS} data-testid="calc-tl">
                    {TIMELINE_OPTS.map((o) => <option key={o}>{o}</option>)}
                  </select>
                </Field>
              </Grid>
              <label style={chkS}>
                <input type="checkbox" required checked={f.consent} onChange={set("consent")}
                       style={{ width: "auto", marginTop: 3 }} data-testid="calc-consent" />
                <span>
                  I agree CORE Realty &amp; Associates LLC may contact me by email, phone or text about my home search.
                  Consent is not a condition of any service. Message/data rates may apply.
                </span>
              </label>
              <button type="submit" disabled={sending} className="calc-cta" style={buttonS} data-testid="calc-submit">
                {sending ? "Sending…" : "Send Me My Plan"}
              </button>
              {err && <div style={{ fontSize: 12, color: "#b00020", marginTop: 8 }} data-testid="calc-err">{err}</div>}
            </form>
          )}
        </Card>

        {/* ─────────────────────────────────────────────────────────────
            Broker-locked disclaimer. Every word below is reproduced
            verbatim from the source HTML — do not shorten or reword.
            ───────────────────────────────────────────────────────────── */}
        <footer
          style={{ textAlign: "center", fontSize: 11, color: "#6b6b6b", marginTop: 24 }}
          data-testid="calculator-disclaimer"
        >
          <p>
            <b>Estimates only.</b> This calculator is for educational purposes and
            is not a loan offer, pre-approval, or commitment to lend. Actual
            qualification depends on credit, lender guidelines, loan program, and
            market conditions. Taxes, insurance, and rates vary by property and
            borrower.
          </p>
          <p>CORE Realty &amp; Associates LLC · Licensed Georgia Real Estate Broker · Equal Housing Opportunity</p>
          <p>Serving Metro Atlanta, and surrounding North Georgia counties</p>
        </footer>
      </main>

      {/* Site footer — matches /clients + /recruit for brand consistency. */}
      <footer className="px-8 py-8 border-t border-[#d1cece]" data-testid="calculator-site-footer">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Logo variant="black" height={32} />
            <span
              className="text-[10px] font-semibold tracking-[0.18em] uppercase text-[#bf858e]"
              data-testid="brand-motto"
            >
              Real Estate Integrity at its CORE
            </span>
          </div>
          <div className="text-xs text-[#050303]/50">
            © {new Date().getFullYear()} CORE Realty and Associates LLC · Snellville, GA
          </div>
        </div>
      </footer>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────────
   Tiny inline-styled primitives — kept close to the broker's source CSS
   so the look matches their printable mockup. Pure React, no new deps.
   ──────────────────────────────────────────────────────────────────── */

const inputS = {
  width: "100%", padding: 11, border: `1px solid ${GREY}`, borderRadius: 8,
  font: "16px Montserrat, Arial, sans-serif", color: INK, background: WHITE,
  boxSizing: "border-box", transition: "border-color 160ms ease, box-shadow 160ms ease",
};
const h2S = {
  fontFamily: "'Sorts Mill Goudy', Georgia, serif", fontWeight: 400, margin: 0,
  fontSize: 23, color: INK, display: "flex", gap: 10, alignItems: "center",
};
const resultS = {
  marginTop: 14, background: SOFT_ROSE, borderLeft: `4px solid ${DUSTY_ROSE}`,
  padding: "12px 14px", borderRadius: 6, color: INK,
};
const bigS = { font: "400 32px 'Sorts Mill Goudy', Georgia, serif", color: DUSTY_ROSE };
const smallS = { fontSize: 12, color: "#6b6b6b" };
const chkS = {
  display: "flex", gap: 8, alignItems: "flex-start",
  fontSize: 12, color: "#6b6b6b", marginTop: 12,
};
const buttonS = {
  background: DUSTY_ROSE, color: WHITE, border: 0, borderRadius: 8,
  padding: 14, font: "600 16px Montserrat, Arial, sans-serif",
  width: "100%", cursor: "pointer", marginTop: 14,
  transition: "background 160ms ease, color 160ms ease, box-shadow 160ms ease",
  letterSpacing: "0.02em",
};
const ghostButtonS = {
  background: WHITE, color: DUSTY_ROSE, border: `2px solid ${DUSTY_ROSE}`,
  borderRadius: 8, padding: "12px 20px",
  font: "600 14px Montserrat, Arial, sans-serif",
  cursor: "pointer", whiteSpace: "nowrap",
  transition: "background 160ms ease, color 160ms ease, box-shadow 160ms ease",
  letterSpacing: "0.02em",
};

function Card({ children, ctaRose = false, ...rest }) {
  return (
    <section
      {...rest}
      style={{
        background: WHITE,
        border: `${ctaRose ? "2px" : "1px"} solid ${ctaRose ? DUSTY_ROSE : GREY}`,
        borderRadius: 12, padding: 18, margin: "16px 0",
      }}
    >
      {children}
    </section>
  );
}

function CardHeading({ num, children }) {
  return (
    <h2 style={h2S}>
      <span style={{
        background: DUSTY_ROSE, color: WHITE, borderRadius: "50%",
        width: 30, height: 30, display: "inline-flex",
        alignItems: "center", justifyContent: "center",
        font: "600 14px Montserrat",
        boxShadow: `0 0 0 4px ${ROSE}66`,
      }}>{num}</span>
      {children}
    </h2>
  );
}

function Grid({ children }) {
  return (
    <div className="calc-grid" style={{
      display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 14,
    }}>{children}</div>
  );
}

function Field({ label, children, fullWidth = false }) {
  return (
    <div style={fullWidth ? { gridColumn: "1 / -1" } : {}}>
      <label style={{ fontSize: 12, fontWeight: 600, color: "#6b6b6b", display: "block", marginBottom: 4 }}>
        {label}
      </label>
      {children}
    </div>
  );
}

function Result({ children }) {
  return <div style={resultS}>{children}</div>;
}
